import type { CatalogProduct } from "@automatry/schemas";

export {
  assertSpecPolicyFlagCatalogCompleteness,
  SPEC_POLICY_FLAG_CATALOG_BY_ID,
  SPEC_POLICY_FLAG_CATALOG_METADATA,
  SPEC_POLICY_FLAG_CATALOG_ORDER,
  SPEC_POLICY_FLAG_CATALOG_V3,
  SPEC_POLICY_FLAG_ROOM_TYPE_LOOP_DEFAULTS
} from "../../../../packages/catalog/src/policyFlagCatalog";
export type {
  SpecPolicyFlagCatalogFamily,
  SpecPolicyFlagValueKind
} from "../../../../packages/catalog/src/policyFlagCatalog";

const normalizePartNo = (partNo: string): string => {
  const normalized = partNo.trim().replace(/\s+/g, " ").toUpperCase();

  if (normalized.length === 0) {
    throw new Error("part number is required");
  }

  return normalized;
};

const loosePartNoKey = (partNo: string): string =>
  normalizePartNo(partNo).replace(/[^A-Z0-9]/g, "");

const addUniqueProductKey = (
  index: Map<string, CatalogProduct | null>,
  key: string,
  product: CatalogProduct
) => {
  if (!index.has(key)) {
    index.set(key, product);
    return;
  }

  const existing = index.get(key);

  if (existing === undefined || existing === null) {
    return;
  }

  if (normalizePartNo(existing.partNo) !== normalizePartNo(product.partNo)) {
    index.set(key, null);
  }
};

const compactIndex = (
  index: ReadonlyMap<string, CatalogProduct | null>
): Map<string, CatalogProduct> =>
  new Map(
    [...index.entries()].filter(
      (entry): entry is [string, CatalogProduct] => entry[1] !== null
    )
  );

const buildProductIndexes = (products: readonly CatalogProduct[]) => {
  const exact = new Map<string, CatalogProduct | null>();
  const loose = new Map<string, CatalogProduct | null>();
  const exactProductPartNoKeys = new Set(
    products.map((product) => normalizePartNo(product.partNo))
  );
  const looseProductPartNoKeys = new Set(
    products.map((product) => loosePartNoKey(product.partNo))
  );

  for (const product of products) {
    addUniqueProductKey(exact, normalizePartNo(product.partNo), product);
    addUniqueProductKey(loose, loosePartNoKey(product.partNo), product);
  }

  for (const product of products) {
    for (const alias of product.aliases) {
      const exactAlias = normalizePartNo(alias);
      const looseAlias = loosePartNoKey(alias);

      if (!exactProductPartNoKeys.has(exactAlias)) {
        addUniqueProductKey(exact, exactAlias, product);
      }

      if (!looseProductPartNoKeys.has(looseAlias)) {
        addUniqueProductKey(loose, looseAlias, product);
      }
    }
  }

  return {
    exact: compactIndex(exact),
    loose: compactIndex(loose)
  };
};

export const findProductByPartNo = (
  products: readonly CatalogProduct[],
  partNo: string
): CatalogProduct | undefined => {
  const indexes = buildProductIndexes(products);
  const exact = indexes.exact.get(normalizePartNo(partNo));

  if (exact) {
    return exact;
  }

  return indexes.loose.get(loosePartNoKey(partNo));
};

export const isHardIoCapabilityEligible = (
  product: Pick<CatalogProduct, "capabilityStatus" | "sourceStatus">
): boolean =>
  product.capabilityStatus === "reviewed" && product.sourceStatus === "known";
